import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AppLayout from "@/layouts/AppLayout";
import Dashboard from "@/pages/Dashboard";
import ProjectView from "@/pages/ProjectView";
import MyTasks from "@/pages/MyTasks";
import Team from "@/pages/Team";
import NewProject from "@/pages/NewProject";
import ProjectsList from "@/pages/ProjectsList";
import HiringRequests from "@/pages/HiringRequests";
import ScopeView from "@/pages/ScopeView";
import Attendance from "@/pages/Attendance";
import HRAttendance from "@/pages/HRAttendance";
import Payroll from "@/pages/Payroll";
import SEOFolder from "@/pages/SEOFolder";
import SidebarList from "@/pages/SidebarList";
import PublishingSchedule from "@/pages/PublishingSchedule";
import PublishingOverview from "@/pages/PublishingOverview";
import SEOPerformance from "@/pages/SEOPerformance";
import MarketingDepartment from "@/pages/MarketingDepartment";
import MarketingTeamView from "@/pages/MarketingTeamView";
import ProductivityReport from "@/pages/ProductivityReport";
import Profile from "@/pages/Profile";
import Home from "@/pages/Home";

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user) return <Navigate to="/dash" replace />;
  return children;
}

// Iter 45.18 — Marketing home lives at "/", the authenticated product lives
// at "/dash". Logged-in visitors on "/" bounce straight to their dashboard
// so they don't re-see the landing page; guests on "/" get the marketing
// site. Guests hitting "/dash" (or any protected route) are sent to /login.
function LandingRoute() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user) return <Navigate to="/dash" replace />;
  return <Home />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingRoute />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/dash" element={<Dashboard />} />
              <Route path="/my-tasks" element={<MyTasks />} />
              <Route path="/team" element={<Team />} />
              <Route path="/projects" element={<ProjectsList />} />
              <Route path="/hiring-requests" element={<HiringRequests />} />
              {/* Iter 45.2 — self-attendance moved into /profile. Old
                  bookmarks / notification deep-links land on Profile so
                  nothing 404s. */}
              <Route path="/attendance" element={<Navigate to="/profile" replace />} />
              <Route path="/hr/attendance" element={<HRAttendance />} />
              <Route path="/team-attendance" element={<HRAttendance />} />
              <Route path="/payroll" element={<Payroll />} />
              {/* /payout kept as an alias for older bookmarks / deep links. */}
              <Route path="/payout" element={<Payroll />} />
              <Route path="/seo" element={<SEOFolder />} />
              <Route path="/seo/folder/:folderId" element={<SEOFolder />} />
              <Route path="/projects/new" element={<NewProject />} />
              <Route path="/projects/:id" element={<ProjectView />} />
              {/* Marketing dept routes — new, independent from the project
                  spreadsheet. The dept landing page is intentionally empty;
                  real work lives in /marketing/teams/:teamId. */}
              <Route path="/scope/department/marketing" element={<MarketingDepartment />} />
              <Route path="/marketing/teams/:teamId" element={<MarketingTeamView />} />
              <Route path="/scope/:kind/:id" element={<ScopeView />} />
              <Route path="/lists/:listId" element={<SidebarList />} />
              <Route path="/publishing/:listId" element={<PublishingSchedule />} />
              <Route path="/publishing-overview" element={<PublishingOverview />} />
              <Route path="/seo-performance" element={<SEOPerformance />} />
              <Route path="/productivity" element={<ProductivityReport />} />
              <Route path="/profile" element={<Profile />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
